// PDF exports should match the rendered dashboard, not a rebuilt table.
// We snapshot the card DOM, hide controls, and only patch export-only issues.
const EXPORT_NODE_ID = 'data-html2canvas-export-id';
const PDF_BLOCK_SELECTOR = '[data-export-pdf-block]';
const EXPORT_SECTION_SELECTOR = '[data-export-section]';
const EXPORT_GRID_GROUP_SELECTOR = '[data-export-grid-group]';
const PDF_A4_LANDSCAPE_WIDTH_PT = 841.89;
const PDF_A4_LANDSCAPE_HEIGHT_PT = 595.28;
const PDF_PAGE_MARGIN_PT = 24;
const PDF_MIN_USEFUL_PAGE_HEIGHT_RATIO = 0.55;
const PDF_IMAGE_FORMAT = 'JPEG';
// JPEG quality 0.9 balances file size with visual fidelity for dashboard charts.
const PDF_IMAGE_QUALITY = 0.9;
const activePdfExports = new WeakSet<HTMLElement>();

type Html2Canvas = typeof import('html2canvas-pro').default;
type JsPDFConstructor = typeof import('jspdf').default;

export interface DownloadElementPdfOptions {
  includedSectionIds?: readonly string[];
}

const loadPdfDependencies = async (): Promise<{
  html2canvas: Html2Canvas;
  JsPDF: JsPDFConstructor;
}> => {
  const [{ default: html2canvas }, { default: JsPDF }] = await Promise.all([
    import('html2canvas-pro'),
    import('jspdf'),
  ]);

  return { html2canvas, JsPDF };
};

const hideExcludedSections = (
  clonedRoot: HTMLElement,
  includedSectionIds: readonly string[] | undefined,
) => {
  if (!includedSectionIds) return;

  const includedSections = new Set(includedSectionIds);

  clonedRoot.querySelectorAll<HTMLElement>(EXPORT_SECTION_SELECTOR).forEach(node => {
    const section = node.getAttribute('data-export-section');
    if (section && !includedSections.has(section)) {
      node.style.display = 'none';
    }
  });
};

const showExportOnlyContent = (clonedRoot: HTMLElement) => {
  clonedRoot.querySelectorAll<HTMLElement>('[data-export-visible]').forEach(node => {
    node.style.display = node.getAttribute('data-export-visible') || 'block';
  });
};

const collapseSingleItemExportGrids = (clonedRoot: HTMLElement) => {
  clonedRoot.querySelectorAll<HTMLElement>(EXPORT_GRID_GROUP_SELECTOR).forEach(group => {
    const visibleChildren = Array.from(group.children).filter(child => {
      if (!(child instanceof HTMLElement)) return false;
      return window.getComputedStyle(child).display !== 'none';
    });

    if (visibleChildren.length === 1) {
      group.style.gridTemplateColumns = 'minmax(0, 1fr)';
    }
  });
};

const expandExportClone = (clonedRoot: HTMLElement) => {
  // Export full content even when the live card uses an inline scroll region.
  clonedRoot.style.height = 'auto';
  clonedRoot.style.maxHeight = 'none';
  clonedRoot.style.overflow = 'visible';

  clonedRoot.querySelectorAll<HTMLElement>('[data-export-scroll-region]').forEach(node => {
    node.style.height = 'auto';
    node.style.maxHeight = 'none';
    node.style.overflow = 'visible';
  });
};

interface CssPageBreaks {
  breaks: number[];
  width: number;
}

export const getCssPageBreaks = (sourceRoot: HTMLElement): CssPageBreaks => {
  const rootRect = sourceRoot.getBoundingClientRect();
  if (rootRect.width <= 0) return { breaks: [], width: 0 };

  const pageContentWidth = PDF_A4_LANDSCAPE_WIDTH_PT - PDF_PAGE_MARGIN_PT * 2;
  const pageContentHeight = PDF_A4_LANDSCAPE_HEIGHT_PT - PDF_PAGE_MARGIN_PT * 2;
  const cssPageHeight = (pageContentHeight / pageContentWidth) * rootRect.width;
  const minUsefulPageHeight = cssPageHeight * PDF_MIN_USEFUL_PAGE_HEIGHT_RATIO;
  const breakableBlocks = Array.from(sourceRoot.querySelectorAll<HTMLElement>(PDF_BLOCK_SELECTOR))
    .map(node => {
      const rect = node.getBoundingClientRect();
      return {
        bottom: rect.bottom - rootRect.top,
        top: rect.top - rootRect.top,
      };
    })
    .filter(block => block.bottom > 0 && block.top < rootRect.height)
    .sort((a, b) => a.top - b.top);

  const breaks: number[] = [];
  let pageStart = 0;

  while (pageStart + cssPageHeight < rootRect.height) {
    const targetBreak = pageStart + cssPageHeight;
    const splitBlock = breakableBlocks
      .filter(block => block.top < targetBreak && block.bottom > targetBreak)
      .sort((a, b) => b.top - a.top)[0];
    const nextBreak =
      splitBlock && splitBlock.top - pageStart >= minUsefulPageHeight
        ? splitBlock.top
        : targetBreak;

    if (nextBreak <= pageStart) break;
    breaks.push(nextBreak);
    pageStart = nextBreak;
  }

  return { breaks, width: rootRect.width };
};

const drawCanvasToPdf = (
  canvas: HTMLCanvasElement,
  filename: string,
  JsPDF: JsPDFConstructor,
  cssPageBreaks: CssPageBreaks,
) => {
  const doc = new JsPDF({ compress: true, orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const imageWidth = pageWidth - PDF_PAGE_MARGIN_PT * 2;
  const imageHeight = pageHeight - PDF_PAGE_MARGIN_PT * 2;
  const sourcePageHeight = Math.floor((imageHeight / imageWidth) * canvas.width);
  const cssToCanvasScale = cssPageBreaks.width > 0 ? canvas.width / cssPageBreaks.width : 1;
  const breakPoints = cssPageBreaks.breaks.map(point => Math.round(point * cssToCanvasScale));
  const pageStarts = [0, ...breakPoints];

  for (let index = 0; index < pageStarts.length; index += 1) {
    const sourceY = pageStarts[index];
    const nextBreak = pageStarts[index + 1] ?? canvas.height;
    const sliceHeight = Math.min(nextBreak - sourceY, sourcePageHeight, canvas.height - sourceY);
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourcePageHeight;

    const context = pageCanvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare PDF page image from the rendered chart.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    context.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );

    let image: string;
    try {
      image = pageCanvas.toDataURL('image/jpeg', PDF_IMAGE_QUALITY);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown canvas serialization error';
      throw new Error(`Unable to prepare PDF image from the rendered chart: ${reason}`);
    }

    if (index > 0) doc.addPage();
    doc.addImage(
      image,
      PDF_IMAGE_FORMAT,
      PDF_PAGE_MARGIN_PT,
      PDF_PAGE_MARGIN_PT,
      imageWidth,
      imageHeight,
    );
  }

  doc.save(filename);
};

export const downloadElementPdf = async (
  element: HTMLElement,
  filename: string,
  options: DownloadElementPdfOptions = {},
) => {
  if (activePdfExports.has(element)) {
    throw new Error('A PDF export is already in progress for this chart.');
  }

  activePdfExports.add(element);

  const exportId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let cssPageBreaks: CssPageBreaks = { breaks: [], width: 0 };

  try {
    await document.fonts?.ready;

    const { html2canvas, JsPDF } = await loadPdfDependencies();
    element.setAttribute(EXPORT_NODE_ID, exportId);

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      onclone: clonedDocument => {
        const clonedRoot = clonedDocument.querySelector<HTMLElement>(
          `[${EXPORT_NODE_ID}="${exportId}"]`,
        );

        clonedDocument.querySelectorAll<HTMLElement>('[data-export-hidden]').forEach(node => {
          node.style.display = 'none';
        });

        if (clonedRoot) {
          hideExcludedSections(clonedRoot, options.includedSectionIds);
          showExportOnlyContent(clonedRoot);
          collapseSingleItemExportGrids(clonedRoot);
          expandExportClone(clonedRoot);
          cssPageBreaks = getCssPageBreaks(clonedRoot);
          clonedRoot.removeAttribute(EXPORT_NODE_ID);
        }
      },
    });

    drawCanvasToPdf(canvas, filename, JsPDF, cssPageBreaks);
  } finally {
    if (element.getAttribute(EXPORT_NODE_ID) === exportId) {
      element.removeAttribute(EXPORT_NODE_ID);
    }
    activePdfExports.delete(element);
  }
};
